
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import PaginationControls from '@/components/shared/suites/PaginationControls.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import ChangeRoleModal from '@/components/user-management/ChangeRoleModal.vue';
import CreateUserModal from '@/components/user-management/CreateUserModal.vue';
import DeleteUserModal from '@/components/user-management/DeleteUserModal.vue';
import UserFiltersModal from '@/components/user-management/UserFiltersModal.vue';

const namespace: string = 'users';

@Component<UserManagement>({
	components: {
		TableHeaders,
		IconButton,
		PaginationControls,
		TextButton,
		TextField,
		WarningModal,
		ChangeRoleModal,
		CreateUserModal,
		DeleteUserModal,
		UserFiltersModal
	}
})
export default class UserManagement extends Vue {
	@Action('fetchUsers', { namespace }) private fetchUsers!: (params?: FetchUsersFilters) => Promise<Auth0User[] | void>;
	@Getter('getUsers', { namespace }) private users!: Auth0User[];
	@Getter('getCount', { namespace }) private count!: number;

	private isFetching: boolean = true;
	private showFiltersModal: boolean = false;
	private showCreateModal: boolean = false;
	private showDeleteModal: boolean = false;
	private showChangeRoleModal: boolean = false;
	private showSelectionLimitModal: boolean = false;
	private userIdsToDelete: string[] = [];
	private selectedUserIds: string[] = [];
	private currentPage: number = 1;
	private checkboxRenderKey: number = 0;
	private readonly itemsPerPage: number = 50;
	private readonly maxSelection: number = 10;
	private searchInput: string = '';
	private sortDirection: 'asc' | 'desc' = 'asc';
	private selectedRoles: string[] = [];

	private get totalPages(): number {
		return Math.ceil(this.count / this.itemsPerPage);
	}

	private get filtersButtonText(): string {
		const rolesCount = this.selectedRoles.length;
		return this.$t('multi_location_settings.user_management.filters.btn_text')
			+ (rolesCount ? ` (${rolesCount})` : '');
	}

	private get tableHeaders(): TableHeader[] {
		const selectedCount = this.selectedUserIds.length;
		return [
			{ text: '', name: 'select-all', class: 'checkbox-col' },
			{
				text: this.$t('multi_location_settings.user_management.table.email')
					+ (selectedCount ? ` (${selectedCount})` : ''),
				name: 'email',
				sort: this.sortDirection
			},
			{ text: this.$t('multi_location_settings.user_management.table.role') },
			{ text: this.$t('multi_location_settings.user_management.table.actions') }
		];
	}

	private get currentPageSelectedCount(): number {
		return this.users.filter(u => this.selectedUserIds.includes(u.user_id)).length;
	}

	private get isCurrentPageFullySelected(): boolean {
		return !!this.users.length && this.currentPageSelectedCount === this.users.length;
	}

	private get isCurrentPagePartiallySelected(): boolean {
		return !!this.currentPageSelectedCount && this.currentPageSelectedCount < this.users.length;
	}

	// Used to force re-mount of the checkbox in the table header
	// It is to prevent the checkbox not visually matching if it should be checked/indeterminate/unchecked
	private get headerCheckboxState(): 'unchecked' | 'indeterminate' | 'checked' {
		if (this.isCurrentPageFullySelected) {
			return 'checked';
		}
		if (this.isCurrentPagePartiallySelected) {
			return 'indeterminate';
		}
		return 'unchecked';
	}

	private created(): void {
		this.loadUsers();
	}

	private beforeDestroy(): void {
		cancelDebounce(this.debouncedSearch);
	}

	// =============================================================================
	// Users fetch, pagination, filters and search
	// =============================================================================

	/**
	 * Call the api to fetch the users with the query parameters
	 *
	 * @return {Promise<void>}
	 */
	private async loadUsers(): Promise<void> {
		this.isFetching = true;
		try {
			await this.fetchUsers({
				page: this.currentPage,
				search: this.searchInput || undefined,
				sortDirection: this.sortDirection,
				role: this.selectedRoles.length ? this.selectedRoles.join(',') : undefined,
				limit: this.itemsPerPage
			});
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.user_management.error_fetching', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isFetching = false;
		}
	}

	private goToPreviousPage(): void {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.loadUsers();
		}
	}

	private goToNextPage(): void {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.loadUsers();
		}
	}

	private onToggleSort(): void {
		this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		this.currentPage = 1;
		this.loadUsers();
	}

	@Debounce(500)
	private debouncedSearch(): void {
		this.currentPage = 1;
		this.loadUsers();
	}

	/**
	 * Remove spaces (as emails don't have spaces) before triggering a search.
	 *
	 * @return {void}
	 */
	private onSearchInput(): void {
		this.searchInput = this.searchInput.replace(/\s+/g, '');
		this.debouncedSearch();
	}

	/**
	 * Reset the table's state before triggering a new fetch.
	 *
	 * @return {void}
	 */
	private clearSearch(): void {
		this.searchInput = '';
		this.currentPage = 1;
		this.loadUsers();
	}

	/**
	 * When filters are applied, check if they're different from the currently applied ones before triggering a new fetch.
	 *
	 * @param {string[]} roles
	 * @return {void}
	 */
	private handleApplyFilters(roles: string[]): void {
		this.showFiltersModal = false;
		const currentSelectedRoles = new Set(this.selectedRoles);
		const filtersDidntChange = roles.length === currentSelectedRoles.size && roles.every(r => currentSelectedRoles.has(r));
		if (filtersDidntChange) {
			return;
		}
		this.selectedRoles = roles;
		this.currentPage = 1;
		this.loadUsers();
	}

	// =============================================================================
	// User actions: create, update, delete
	// =============================================================================

	/**
	 * Close the create modal and refresh the list.
	 *
	 * @return {void}
	 */
	private handleUserCreated(): void {
		this.showCreateModal = false;
		Toast.success(this.$t('multi_location_settings.user_management.create.success'));
		this.loadUsers();
	}

	/**
	 * Open the delete confirmation modal, either for a single user or a bulk delete.
	 *
	 * @param {string} [userId]
	 * @return {void}
	 */
	private openDeleteModal(userId?: string): void {
		this.userIdsToDelete = userId ? [userId] : [...this.selectedUserIds];
		this.showDeleteModal = true;
	}

	/**
	 * Close the delete modal and refresh the list.
	 *
	 * @return {void}
	 */
	private handleUserDeleted(): void {
		this.showDeleteModal = false;
		// Only single-user delete needs the toast. Bulk actions have the summary view.
		if (this.userIdsToDelete.length === 1) {
			Toast.success(this.$t('multi_location_settings.user_management.delete.success'));
		}
		this.selectedUserIds = [];
		this.loadUsers();
	}

	/**
	 * Close the change-role modal and refresh the list after the user dismisses the summary.
	 *
	 * @return {void}
	 */
	private handleRoleChanged(): void {
		this.showChangeRoleModal = false;
		this.selectedUserIds = [];
		this.loadUsers();
	}

	// =============================================================================
	// Multi-user selection
	// =============================================================================

	/**
	 * Check whether a user is currently in the bulk selection.
	 *
	 * @param {string} userId
	 * @return {boolean}
	 */
	private isUserSelected(userId: string): boolean {
		return this.selectedUserIds.includes(userId);
	}

	/**
	 * Add or remove a user from the bulk selection.
	 *
	 * @param {string} userId
	 * @return {void}
	 */
	private toggleUserSelection(userId: string): void {
		// Remove user from selection if already selected
		const index = this.selectedUserIds.indexOf(userId);
		if (index >= 0) {
			this.selectedUserIds.splice(index, 1);
			return;
		}

		// Prevent selection if limit is reached.
		// The checkboxRenderKey is used to force re-mount of the checkboxes
		// It is to prevent the checkbox being visually checked even if we prevent the userId from being pushed to the array.
		if (this.selectedUserIds.length >= this.maxSelection) {
			this.showSelectionLimitModal = true;
			this.checkboxRenderKey++;
			return;
		}

		this.selectedUserIds.push(userId);
	}

	/**
	 * Handle click on the checkbox in the table headers to select/unselect multiple users depending
	 * of if users are already selected on the current page or not.
	 *
	 * @return {void}
	 */
	private toggleCurrentPageSelection(): void {
		const currentPageUserIds = this.users.map(u => u.user_id);

		// If any user on the current page is already selected, treat the click as "clear this page".
		if (this.currentPageSelectedCount > 0) {
			this.selectedUserIds = this.selectedUserIds.filter(id => !currentPageUserIds.includes(id));
			return;
		}

		const slotsRemaining = this.maxSelection - this.selectedUserIds.length;

		// If we can't add all the users on the page due to the limit, we add up to it and show a warning
		if (currentPageUserIds.length > slotsRemaining) {
			this.selectedUserIds = [...this.selectedUserIds, ...currentPageUserIds.slice(0, slotsRemaining)];
			this.showSelectionLimitModal = true;
			this.checkboxRenderKey++;
			return;
		}

		// Select all the users on the page
		this.selectedUserIds = [...this.selectedUserIds, ...currentPageUserIds];
	}
}
